// Iter 82 / Phase E1.0 — Holiday Calendar admin page.
// List + detail with nested holiday entries CRUD. Multiple calendars
// per fiscal year supported; one default per FY auto-assigned to new
// employees.
import { useCallback, useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, FloppyDisk, X, ArrowLeft, Calendar as CalendarIcon } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";

const EMPTY_CAL = { code: "", name: "", description: "", fiscal_year: "", is_default: false, is_active: true };
const EMPTY_ENTRY = { date: "", observed_date: "", name: "", description: "", is_optional: false, is_restricted: false, is_active: true };

export default function HolidayCalendars() {
    const { can } = useAuth();
    const [calendars, setCalendars] = useState([]);
    const [fys, setFys] = useState([]);
    const [selected, setSelected] = useState(null);
    const [calDialog, setCalDialog] = useState(null);  // null | "new" | row
    const [calDraft, setCalDraft] = useState(EMPTY_CAL);
    const [entryDialog, setEntryDialog] = useState(null); // null | "new" | row
    const [entryDraft, setEntryDraft] = useState(EMPTY_ENTRY);

    const reload = useCallback(async () => {
        try {
            const [c, m] = await Promise.all([
                api.get("/leave/holiday-calendar/list"),
                api.get("/leave/meta"),
            ]);
            setCalendars(c.data.items || []);
            setFys(m.data.fiscal_years || []);
            if (selected) {
                const fresh = (c.data.items || []).find((x) => x.id === selected.id);
                if (fresh) setSelected(fresh);
            }
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load calendars"));
        }
    }, [selected]);

    useEffect(() => { Promise.resolve().then(() => { reload(); }); /* eslint-disable-next-line */ }, []);

    const openCalCreate = () => {
        const currentFy = fys.find((f) => f.is_current);
        setCalDraft({ ...EMPTY_CAL, fiscal_year: currentFy?.code || "" });
        setCalDialog("new");
    };
    const openCalEdit = (cal) => {
        setCalDraft({ ...EMPTY_CAL, ...cal });
        setCalDialog(cal);
    };

    const saveCal = async () => {
        try {
            const body = { ...calDraft, code: (calDraft.code || "").toUpperCase() };
            if (calDialog === "new") {
                await api.post("/leave/holiday-calendar", body);
                toast.success("Calendar created.");
            } else {
                await api.patch(`/leave/holiday-calendar/${calDialog.id}`, body);
                toast.success("Calendar updated.");
            }
            setCalDialog(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };

    const deleteCal = async (cal) => {
        if (!window.confirm(`Delete calendar "${cal.name}"?`)) return;
        try {
            await api.delete(`/leave/holiday-calendar/${cal.id}`);
            toast.success("Deleted.");
            if (selected?.id === cal.id) setSelected(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    const saveEntry = async () => {
        // Owner-locked 2026-02-27 · normalize empty observed_date to null
        // so the backend Optional[str] validator accepts it (empty string
        // would fail the ISO-date guard).
        const body = {
            ...entryDraft,
            observed_date: entryDraft.observed_date && entryDraft.observed_date !== entryDraft.date
                ? entryDraft.observed_date
                : null,
        };
        try {
            if (entryDialog === "new") {
                await api.post(`/leave/holiday-calendar/${selected.id}/entry`, body);
                toast.success("Holiday added.");
            } else {
                await api.patch(`/leave/holiday-calendar/${selected.id}/entry/${entryDialog.id}`, body);
                toast.success("Holiday updated.");
            }
            setEntryDialog(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };

    const deleteEntry = async (entry) => {
        if (!window.confirm(`Delete entry "${entry.name}" on ${entry.date}?`)) return;
        try {
            await api.delete(`/leave/holiday-calendar/${selected.id}/entry/${entry.id}`);
            toast.success("Deleted.");
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    // List view
    if (!selected) {
        return (
            <div className="px-6 sm:px-8 py-6 max-w-[1200px]" data-testid="holiday-calendars-page">
                <PageHeader
                    eyebrow="Leave Management Configuration"
                    title="Holiday Calendars"
                    description="Multiple calendars supported. Employees map to one calendar; the default per fiscal year auto-applies to new employees."
                    actions={can("leave:admin") && (
                        <Button onClick={openCalCreate} data-testid="hcal-new-btn"><Plus size={14} className="mr-1" /> New Calendar</Button>
                    )}
                />
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {calendars.length === 0 && (
                        <div className="col-span-full bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-500">
                            No holiday calendars yet. Create one to begin tracking holidays.
                        </div>
                    )}
                    {calendars.map((c) => (
                        <button key={c.id} data-testid={`hcal-card-${c.id}`} onClick={() => setSelected(c)} className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-indigo-300 hover:shadow-sm transition-all">
                            <div className="flex items-center gap-2 mb-1">
                                <CalendarIcon size={16} className="text-indigo-600" />
                                <span className="font-semibold text-slate-800">{c.name}</span>
                            </div>
                            <div className="text-xs text-slate-500 font-mono mb-2">{c.code} · {c.fiscal_year}</div>
                            <div className="flex flex-wrap gap-1 text-xs">
                                {c.is_default && <Badge className="bg-emerald-100 text-emerald-700">Default</Badge>}
                                {!c.is_active && <Badge variant="secondary">Inactive</Badge>}
                                <Badge variant="outline">{(c.entries || []).length} holidays</Badge>
                            </div>
                            {c.description && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{c.description}</p>}
                        </button>
                    ))}
                </div>

                {/* Cal create dialog */}
                <Dialog open={!!calDialog} onOpenChange={(o) => { if (!o) setCalDialog(null); }}>
                    <DialogContent data-testid="hcal-dialog">
                        <DialogHeader><DialogTitle>{calDialog === "new" ? "New Calendar" : "Edit Calendar"}</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label className="text-xs">Code (uppercase)</Label><Input data-testid="hcal-code" value={calDraft.code} onChange={(e) => setCalDraft({ ...calDraft, code: e.target.value.toUpperCase() })} placeholder="MAHA" disabled={calDialog !== "new"} /></div>
                                <div><Label className="text-xs">Fiscal Year</Label>
                                    <select data-testid="hcal-fy" className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={calDraft.fiscal_year} onChange={(e) => setCalDraft({ ...calDraft, fiscal_year: e.target.value })}>
                                        <option value="">— select —</option>
                                        {fys.map((f) => <option key={f.id} value={f.code}>{f.code}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div><Label className="text-xs">Name</Label><Input data-testid="hcal-name" value={calDraft.name} onChange={(e) => setCalDraft({ ...calDraft, name: e.target.value })} placeholder="Maharashtra Holidays" /></div>
                            <div><Label className="text-xs">Description</Label><Textarea rows={2} value={calDraft.description} onChange={(e) => setCalDraft({ ...calDraft, description: e.target.value })} /></div>
                            <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="hcal-default" checked={calDraft.is_default} onChange={(e) => setCalDraft({ ...calDraft, is_default: e.target.checked })} /> Default for this fiscal year (only one)</label>
                            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={calDraft.is_active !== false} onChange={(e) => setCalDraft({ ...calDraft, is_active: e.target.checked })} /> Active</label>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setCalDialog(null)}>Cancel</Button>
                            <Button onClick={saveCal} data-testid="hcal-save"><FloppyDisk size={14} className="mr-1" /> Save</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    // Calendar detail view
    const entries = (selected.entries || []).slice().sort((a, b) => a.date.localeCompare(b.date));
    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1200px]" data-testid="holiday-calendar-detail">
            <PageHeader
                eyebrow={`Holiday Calendar · ${selected.code}`}
                title={selected.name}
                description={`${selected.fiscal_year} · ${entries.length} holiday entries`}
                actions={(
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setSelected(null)} data-testid="hcal-back-btn"><ArrowLeft size={14} className="mr-1" /> Back</Button>
                        {can("leave:admin") && <Button variant="outline" onClick={() => openCalEdit(selected)} data-testid="hcal-edit-btn"><PencilSimple size={14} className="mr-1" /> Edit</Button>}
                        {can("leave:admin") && <Button variant="outline" onClick={() => deleteCal(selected)} className="text-rose-500"><Trash size={14} className="mr-1" /> Delete</Button>}
                    </div>
                )}
            />

            <section className="mt-6 bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold">Holiday Entries</h3>
                    {can("leave:admin") && (
                        <Button size="sm" data-testid="hcal-entry-new-btn" onClick={() => { setEntryDraft(EMPTY_ENTRY); setEntryDialog("new"); }}>
                            <Plus size={14} className="mr-1" /> Add Holiday
                        </Button>
                    )}
                </div>
                <table className="w-full text-sm" data-testid="hcal-entries-table">
                    <thead className="bg-slate-50 text-slate-600 text-xs">
                        <tr>
                            <th className="text-left px-3 py-2 font-medium">Date</th>
                            <th className="text-left px-3 py-2 font-medium">Holiday</th>
                            <th className="text-left px-3 py-2 font-medium">Description</th>
                            <th className="text-left px-3 py-2 font-medium">Flags</th>
                            <th className="w-24" />
                        </tr>
                    </thead>
                    <tbody>
                        {entries.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No holiday entries. Add the first one.</td></tr>}
                        {entries.map((e) => (
                            <tr key={e.id} className="border-t border-slate-100">
                                <td className="px-3 py-2 font-mono text-xs text-slate-700" data-testid={`hcal-entry-date-${e.id}`}>
                                    {e.date}
                                    {e.observed_date && e.observed_date !== e.date && (
                                        <div
                                            className="text-[10px] font-normal text-amber-700 mt-0.5"
                                            data-testid={`hcal-entry-observed-${e.id}`}
                                        >
                                            observed → {e.observed_date}
                                        </div>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-slate-800 font-medium">{e.name}</td>
                                <td className="px-3 py-2 text-xs text-slate-500">{e.description || "—"}</td>
                                <td className="px-3 py-2">
                                    <div className="flex flex-wrap gap-1">
                                        {e.is_optional && <Badge variant="outline">Optional</Badge>}
                                        {e.is_restricted && <Badge className="bg-amber-100 text-amber-700">Restricted</Badge>}
                                        {!e.is_active && <Badge variant="secondary">Inactive</Badge>}
                                    </div>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {can("leave:admin") && (
                                        <>
                                            <button onClick={() => { setEntryDraft(e); setEntryDialog(e); }} className="text-slate-500 hover:text-slate-700 mr-2"><PencilSimple size={14} /></button>
                                            <button onClick={() => deleteEntry(e)} className="text-rose-500 hover:text-rose-700"><Trash size={14} /></button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            {/* Entry dialog */}
            <Dialog open={!!entryDialog} onOpenChange={(o) => { if (!o) setEntryDialog(null); }}>
                <DialogContent data-testid="hcal-entry-dialog">
                    <DialogHeader><DialogTitle>{entryDialog === "new" ? "Add Holiday" : "Edit Holiday"}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label className="text-xs">Statutory date</Label><Input data-testid="entry-date" type="date" value={entryDraft.date} onChange={(e) => setEntryDraft({ ...entryDraft, date: e.target.value })} /></div>
                        <div>
                            <Label className="text-xs">Observed date <span className="text-slate-400 font-normal">(if REEDOS closed a different day)</span></Label>
                            <Input
                                data-testid="entry-observed-date"
                                type="date"
                                value={entryDraft.observed_date || ""}
                                onChange={(e) => setEntryDraft({ ...entryDraft, observed_date: e.target.value })}
                            />
                            <div className="text-[10px] text-slate-400 mt-1">Leave blank if same as statutory date. If set, REEDOS is treated as open on the statutory date and closed on the observed date.</div>
                        </div>
                        <div><Label className="text-xs">Holiday name</Label><Input data-testid="entry-name" value={entryDraft.name} onChange={(e) => setEntryDraft({ ...entryDraft, name: e.target.value })} placeholder="Independence Day" /></div>
                        <div><Label className="text-xs">Description</Label><Textarea rows={2} value={entryDraft.description} onChange={(e) => setEntryDraft({ ...entryDraft, description: e.target.value })} /></div>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!entryDraft.is_optional} onChange={(e) => setEntryDraft({ ...entryDraft, is_optional: e.target.checked })} /> Optional holiday</label>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!entryDraft.is_restricted} onChange={(e) => setEntryDraft({ ...entryDraft, is_restricted: e.target.checked })} /> Restricted (employees pick N of M)</label>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={entryDraft.is_active !== false} onChange={(e) => setEntryDraft({ ...entryDraft, is_active: e.target.checked })} /> Active</label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEntryDialog(null)}><X size={14} className="mr-1" /> Cancel</Button>
                        <Button onClick={saveEntry} data-testid="entry-save"><FloppyDisk size={14} className="mr-1" /> Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cal edit dialog */}
            <Dialog open={!!calDialog} onOpenChange={(o) => { if (!o) setCalDialog(null); }}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Edit Calendar</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label className="text-xs">Code</Label><Input value={calDraft.code} disabled /></div>
                        <div><Label className="text-xs">Name</Label><Input value={calDraft.name} onChange={(e) => setCalDraft({ ...calDraft, name: e.target.value })} /></div>
                        <div><Label className="text-xs">Description</Label><Textarea rows={2} value={calDraft.description} onChange={(e) => setCalDraft({ ...calDraft, description: e.target.value })} /></div>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={calDraft.is_default} onChange={(e) => setCalDraft({ ...calDraft, is_default: e.target.checked })} /> Default for FY</label>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={calDraft.is_active !== false} onChange={(e) => setCalDraft({ ...calDraft, is_active: e.target.checked })} /> Active</label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCalDialog(null)}>Cancel</Button>
                        <Button onClick={saveCal}><FloppyDisk size={14} className="mr-1" /> Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
